'use client';

import { usePathname } from 'next/navigation';
import { Button } from '@highereducation/hen-ui';
import { announceLogout, beginLogout } from '@hen-site/lib/auth/accountState';
import { LEARNERS_HUB_SEGMENT } from '@hen-site/lib/learnersHubUtils';

export interface TenantSignOutLinkProps {
  tenant: string;
}

function keepsThisDocumentOpen(event: {
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  readonly button: number;
}): boolean {
  return (
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.button !== 0
  );
}

/** Tells other tabs before the browser follows `href`. */
export function SignOutLink({ href }: { readonly href: string }) {
  return (
    <Button
      variant='grayOutline'
      size='sm'
      href={href}
      onClick={(event) => {
        if (keepsThisDocumentOpen(event)) {
          announceLogout();
          return;
        }
        beginLogout();
      }}
    >
      Sign out
    </Button>
  );
}

/**
 * Sign out preserves the current path: `/[tenant]/logout/?returnTo=…` is validated server-side.
 * For learners-hub routes, returnTo points to the hub root so guests aren't
 * bounced to login after signing out of an auth-required section.
 */
export function TenantSignOutLink({ tenant }: TenantSignOutLinkProps) {
  const pathname = usePathname() ?? '';
  const hubPrefix = `/${tenant}/${LEARNERS_HUB_SEGMENT}`;
  const isLearnersHub = pathname.startsWith(hubPrefix);
  const path = isLearnersHub
    ? `${hubPrefix}/`
    : pathname.startsWith('/')
    ? pathname
    : `/${tenant}/`;
  const returnTo = encodeURIComponent(path);
  const href = `/${tenant}/logout/?returnTo=${returnTo}`;

  return <SignOutLink href={href} />;
}
